// Copyright 2019 Sorama B.V.

import { BindingEngine, Disposable, inject, transient } from "aurelia-framework";
import { Router } from "aurelia-router";
import { TechnicalTroubleshootingGuidanceCatalog } from "catalogs/GuidanceCatalog/TechnicalTroubleshootingGuidanceCatalog";
import { ClientWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/ClientWorkflowModuleCatalog";
import { AcquisitionServiceSettings, CaptureDevice, FrontEnd, VersionInfo } from "datatypes/SoramaLocalApi";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { Browser, MediaQueries } from "libraries/MediaQueries/MediaQueries";
import { IWorkflowModuleLifetime } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { StorageRepository } from "repositories/StorageRepository";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { CachedMeasurementDeviceManagement } from "services/Hardware/Management/CachedMeasurementDeviceManagement";
import { MeasurementDeviceService } from "services/Hardware/MeasurementDeviceService";
import { IServerStatusService, ServerStatus } from "services/Server/IServerStatusService";
import { IStorageService } from "services/Storage/IStorageService";

import { ILicenseService } from "services/License/ILicenseService";
class MeasurementDeviceStatus<TDevice> {
    public device: TDevice;
    public isConnected: boolean;
    public isAvailableOffline: boolean;
    public isIV64: boolean;
}

/**
 * MODULE OPERATION DESCRIPTION - ClientStatusModule (Sorama Services)
 * Shows the current status of the client
 * Other properties that are controlled in this module are (workflowproperty or router paths):
 * - None as of yet
 */
@transient()
@inject(
    IClientService,
    "IHardwareService<FrontEnd>",
    CamIV64Service,
    "IHardwareService<CaptureDevice>",
    CachedMeasurementDeviceManagement,
    MeasurementDeviceService,
    IStorageService,
    IAuthentication,
    Router,
    BindingEngine,
    IServerStatusService,
    StorageRepository,
    ILicenseService,
    ShellViewManager,
    MediaQueries
)
//@autoinject()
export class ClientServerStatusModule implements IWorkflowModuleLifetime {
    //#region State Properties
    private subscriptions: Disposable[];
    private hardwareRefreshInterval: number;
    private frontEnds: MeasurementDeviceStatus<FrontEnd>[];

    public storagePath: string;
    public initialized: boolean;
    public selectedRecording: unknown;

    public installedVersion: VersionInfo;
    public loading: boolean;
    public storagePathError: string;
    public errorMessage: string;

    public bytesUsedInCloud: number;
    private bindPromise: Promise<void>;

    public get isServerOffline(): boolean {
        return this.serverStatusService.status === ServerStatus.Offline;
    }

    public get isServerUnreachable(): boolean {
        return this.serverStatusService.status === ServerStatus.Unreachable;
    }

    public get isServerOnline(): boolean {
        return this.serverStatusService.status === ServerStatus.Online;
    }

    public get isClientAvailable(): boolean {
        return this.clientService.status === ClientStatus.Available;
    }

    public get isClientNotAvailable(): boolean {
        return this.clientService.status === ClientStatus.NotAvailable;
    }

    public get isClientOutdated(): boolean {
        return this.clientService.status === ClientStatus.OutdatedVersion;
    }

    public get isClientUnsupported(): boolean {
        return this.clientService.status === ClientStatus.UnsupportedVersion;
    }

    //#endregion

    //#region Input Properties
    //#endregion

    constructor(
        private readonly clientService: IClientService,
        private readonly frontEndService: IHardwareService<FrontEnd>,
        private readonly camService: CamIV64Service,
        private readonly captureDeviceService: IHardwareService<CaptureDevice>,
        private readonly cachedMeasurementDeviceService: CachedMeasurementDeviceManagement,
        private readonly measurementDeviceService: MeasurementDeviceService,
        private readonly storageService: IStorageService,
        private readonly authentication: IAuthentication,
        private readonly router: Router,
        private readonly bindingEngine: BindingEngine,
        private readonly serverStatusService: IServerStatusService,
        private readonly storageRepository: StorageRepository,
        private readonly licenseService: ILicenseService,
        private readonly shellviewManager: ShellViewManager,
        private readonly mediaQueries: MediaQueries
    ) {
        this.mediaQueries = mediaQueries;
    }

    //#region Lifecycle functions
    public activateModule(): void {
        this.hardwareRefreshInterval = -1;

        // Register a listener on acquisition service status
        this.subscriptions = [
            this.bindingEngine.propertyObserver(this.clientService, "settings").subscribe(this.onSettingsChanged),
            this.bindingEngine.propertyObserver(this.clientService, "status").subscribe(this.onClientStatusChanged),
            this.bindingEngine.collectionObserver(this.frontEndService.hardware).subscribe(this.onDevicesChanged),
            this.bindingEngine.collectionObserver(this.captureDeviceService.hardware).subscribe(this.onDevicesChanged),
            this.bindingEngine
                .collectionObserver(this.measurementDeviceService.hardware)
                .subscribe(this.onDevicesChanged),
            this.bindingEngine.collectionObserver(this.camService.hardware).subscribe(this.onDevicesChanged),
            this.bindingEngine.propertyObserver(this.camService, "isRefreshing").subscribe(this.onDevicesChanged),
        ];

        this.serverStatusService.updateStatus();

        // Get the total storage for this user (no need to wait for it)
        this.storageRepository
            .getStorageSize()
            .then((x) => {
                this.bytesUsedInCloud = x;
            })
            .catch((e) => {
                console.error(e);
            });

        // Make sure the acquisition service settings are updated
        this.bindPromise = this.clientService
            .updateStatus()
            .then((status) => {
                if (status === ClientStatus.Available || status === ClientStatus.OutdatedVersion) {
                    // Update other settings
                    this.clientService.updateSettings().catch(() => {});
                    this.clientService.updateStorage().catch(() => {});
                }

                if (this.onClientStatusChanged(status, undefined)) {
                    this.initialized = true;
                }
            })
            .catch(() => {});

        let refreshInterval = 5000;

        const refresh = async () => {
            await this.camService.refresh();

            // increase the interval by 5 seconds each time, up to 10 seconds
            refreshInterval = Math.min(refreshInterval + 5000, 10000);

            // schedule the next refresh
            setTimeout(refresh, refreshInterval);
        };

        // start the first refresh
        refresh();
    }

    public deactivateModule(): void {
        this.bindPromise.then(() => {
            this.subscriptions.forEach((x) => x.dispose());
            this.subscriptions = [];

            if (this.hardwareRefreshInterval !== -1) {
                window.clearInterval(this.hardwareRefreshInterval);
                this.hardwareRefreshInterval = -1;
            }
        });
    }

    private onClientStatusChanged = (
        status: ClientStatus,
        oldStatus?: ClientStatus,
        preventNavigate?: boolean
    ): boolean => {
        if (status !== ClientStatus.NotAvailable && status !== ClientStatus.UnsupportedVersion) {
            // Update client related stats
            this.clientService
                .getInstalledVersion()
                .then((x) => {
                    this.installedVersion = x;
                })
                .catch((x) => {
                    this.errorMessage = "Unable to connect to the acquisition service";
                });

            this.hardwareRefreshInterval = window.setInterval(this.intervalRefreshHardware, 30000);
            this.refreshHardware();
            return true;
        } else {
            return false;
        }
    };

    private onSettingsChanged = (settings: AcquisitionServiceSettings) => {
        if (!this.storagePathError) this.storagePath = settings.storagePath;
    };

    private onDevicesChanged = (): void => {
        // Cross reference front ends with measurement devices
        let cachedDevicePromise: Promise<MeasurementDevice[]>;

        // This will also return not connected cached devices!
        cachedDevicePromise = this.cachedMeasurementDeviceService.getCachedMeasurementDevices().catch((e) => {
            console.error(e);

            // Continue without cached devices
            return [];
        });

        // Make sure that cached devices call is ready
        cachedDevicePromise.then((cachedDevices) => {
            this.frontEnds = [];

            // Determine connected devices
            for (let i = 0; i < this.frontEndService.hardware.length; i++) {
                const frontEnd = this.frontEndService.hardware[i];

                // See if connected device is also cached, if so, add the cached frontend (with correct name) to the list
                const cachedDevice = cachedDevices.find((dev) => dev.frontEnd.id === frontEnd.id);

                this.frontEnds.push(<MeasurementDeviceStatus<FrontEnd>>{
                    device: cachedDevice ? cachedDevice.frontEnd : frontEnd,
                    isAvailableOffline: cachedDevice ? true : false,
                    isConnected: true,
                    isIV64: false,
                });
            }

            // Determine cached devices
            for (let i = 0; i < cachedDevices.length; i++) {
                const cachedDevice = cachedDevices[i];
                if (this.frontEnds.some((dev) => dev.device.id === cachedDevice.frontEnd.id)) {
                    // Device is connected, skip
                    continue;
                }

                // Device is cached but not connected, add it
                this.frontEnds.push(<MeasurementDeviceStatus<FrontEnd>>{
                    device: cachedDevice.frontEnd,
                    isAvailableOffline: true,
                    isConnected: false,
                    isIV64: false,
                });
            }

            // Add HTTP connected CAMs
            for (const cam of this.camService.hardware) {
                this.frontEnds.push({
                    device: cam as unknown as FrontEnd,
                    isAvailableOffline: false,
                    isConnected: true,
                    isIV64: true,
                });
            }
        });
    };

    //#endregion

    //#region View related functions

    public selectStoragePath = () => {
        this.storagePathError = null;

        this.clientService
            .getFolderPath()
            .then((newPath) => {
                if (newPath) {
                    this.updateStoragePath(newPath);
                }
            })
            .catch((e) => {
                this.storagePathError = e.message;
            });
    };

    private updateStoragePath = (newPath?: string) => {
        this.storagePathError = null;

        if (newPath) {
            this.storagePath = null;
            this.clientService.settings.storagePath = newPath;
            return this.clientService
                .updateSettings(this.clientService.settings)
                .then(() => {
                    this.storagePath = this.clientService.settings.storagePath;
                })
                .catch((e) => {
                    this.storagePathError = e.message ? e.message : e;
                });
        } else {
            this.storagePath = this.clientService.settings.storagePath;
        }
    };

    public openPathInExplorer(path: string): void {
        this.storageService.openInExplorer(path);
    }

    private intervalRefreshHardware = () => {
        this.loading = true;
        const measurementDeviceServiceRefresh = this.measurementDeviceService.refresh().catch(() => {});
        return measurementDeviceServiceRefresh.then(() => {
            this.loading = false;
        });
    };

    private refreshHardware = () => {
        this.loading = true;
        const measurementDeviceServiceRefresh = this.measurementDeviceService.refresh().catch(() => {});
        const camServiceRefresh = this.camService.refresh().catch(() => {});
        return Promise.all([measurementDeviceServiceRefresh, camServiceRefresh]).then(() => {
            this.loading = false;
        });
    };

    private navigateToHelp(): void {
        if (this.mediaQueries.currentBrowser !== Browser.Firefox) {
            this.shellviewManager.openRightSideBarHelp(
                TechnicalTroubleshootingGuidanceCatalog.TechnicalTroubleshootingGS2Module.name
            );
        } else {
            this.shellviewManager.openRightSideBarHelp(
                TechnicalTroubleshootingGuidanceCatalog.TechnicalTroubleshootingGS2FirefoxModule.name
            );
        }
    }

    private navigateWorkflowToModule(moduleName: string): void {
        this.shellviewManager.openRightSideBar(moduleName);
    }

    private openDeviceManagement(frontendStatus: MeasurementDeviceStatus<FrontEnd>) {
        this.shellviewManager.openRightSideBar(ClientWorkflowModuleCatalog.DeviceManagementModule, [
            {
                name: "frontEndId",
                value: frontendStatus.device.id,
            },
        ]);
    }
    //#endregion
}
